/* eslint-disable @typescript-eslint/naming-convention */
import {
  PressKitGuidelineExtensionEnum,
  PressKitProfileExtensionEnum,
} from '@openapi/prtimes-admin-company';

export const API_URL = '/api';
export const FOLLOWING_URL = '/api/media_receive_edit.php';
export const SUPPORTER_API = '/api/media_follow.php?m=status&company_id=';
export const SEARCH_LIMIT_PC = 40;
export const SEARCH_LIMIT_SP = 30;

// company_id の次の値を取得して number に変換する
const paths = location.pathname.split('/');
const companyIdPathIndex = paths.indexOf('company_id');
export const COMPANY_ID =
  // eslint-disable-next-line n/prefer-global/process
  process.env.NODE_ENV === 'mock'
    ? 112
    : Number.parseInt(paths[companyIdPathIndex + 1], 10);

export const DEFAULT_PAGE_NUM = 1;

/**
 * 一般ユーザー用のプレスキットURL
 */
export const PRESS_KIT_URL = '/api/press_kit.php';
/**
 * Editor
 */
export const PRESS_RELEASE_EDITOR_SAVE_TIME_URL =
  '/api/company_release_savetime.php';
export const PRESS_RELEASE_EDITOR_AUTO_SAVE_URL =
  '/api/company_release_autosave.php';
export const PRESS_RELEASE_EDITOR_UPLOAD_URL = '/api/upload.php';

/**
 * 企業管理用のプレスキットURL
 */
export const PRESS_KIT_ADMIN_URL = '/api/press_kit_edit.php';

// 本番・開発用ファイルアップロード完了確認用APIのカタログ
const CHECK_FILE_PUT_COMPLETED_API_CATALOG = {
  prod: 'https://wislxdd5w6.execute-api.ap-northeast-1.amazonaws.com/v1/check_file_put_completed',
  dev: 'https://vonvgky2p0.execute-api.ap-northeast-1.amazonaws.com/v1/check_file_put_completed',
};

/**
 * ファイルアップロード完了確認用API
 *
 * 仕様:https://www.notion.so/API-ca045cec0e6946ad9a36eb294bd21d3f
 */
export const CHECK_FILE_PUT_COMPLETED_API =
  location.hostname === 'prtimes.jp'
    ? CHECK_FILE_PUT_COMPLETED_API_CATALOG.prod
    : CHECK_FILE_PUT_COMPLETED_API_CATALOG.dev;

// 本番・開発用Fastlyホスト名のカタログ
const FASTLY_HOSTNAME_CATALOG = {
  prod: 'prcdn.freetls.fastly.net',
  dev: 'prcdn-dev.freetls.fastly.net',
};

/**
 * Fastlyのホスト名
 */
export const FASTLY_HOSTNAME =
  location.hostname === 'prtimes.jp'
    ? FASTLY_HOSTNAME_CATALOG.prod
    : FASTLY_HOSTNAME_CATALOG.dev;

/**
 * 料金システム用のAPI URL
 */
export const PRICE_URL = '/api/price.php';

// eslint-disable-next-line n/prefer-global/process
export const AUTH_URL = process.env.PUBLIC_AUTH_URL || '';
// eslint-disable-next-line n/prefer-global/process
export const S3_URL = process.env.PUBLIC_S3_URL || '';
export const PRTIMES_FRONTEND_URL =
  // eslint-disable-next-line n/prefer-global/process
  process.env.PUBLIC_PRTIMES_FRONTEND_URL || '';

export const COMPANY_DETAIL_TAB_INDEX = {
  PRESS_RELEASE: {
    PC: 0,
    SP: 0,
  },
  STORY: {
    PC: 1,
    SP: 1,
  },
  INFO: {
    SP: 2,
  },
  PRESS_KIT: {
    PC: 2,
    SP: 3,
  },
} as const;

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
export const EXPERIMENTS: {
  press_kit_bulk_download: boolean;
  use_editor_release_history: boolean; // エディターの変更履歴機能を使用できるようにする
  use_editor_table_replace: boolean; // エディターの新しい表機能を使用できるようにする
} = {
  ...JSON.parse(localStorage.getItem('experiments') ?? '{}'),
  press_kit_bulk_download: true,
  use_replaced_company_press_release_api: true,
};

// 企業タブの中にプレスキットタブが入っている構成なので
// それぞれの値が被って干渉しないようプレスキットタブは10からにしています
export const PRESS_KIT_TAB_INDEX = {
  LOGO: 10,
  IMAGE: 11,
  DOCUMENT: 12,
  PROFILE: 13,
  GUIDELINE: 14,
} as const;

export const DEFAULT_THUMBNAIL = {
  PROFILE: {
    URI: `${S3_URL}/press_kit/common/profile-thumbnail-default.png`,
    FILE_SIZE: 7213,
    EXTENSION: PressKitProfileExtensionEnum.Png,
  },
  EXAMPLE_LOGO: {
    URI: `${S3_URL}/press_kit/common/default-thumbnails/default-logo-thumbnail.png`,
    FILE_SIZE: 5094,
    EXTENSION: PressKitProfileExtensionEnum.Png,
  },
  EXAMPLE_IMAGE: {
    URI: `${S3_URL}/press_kit/common/default-thumbnails/default-image-thumbnail.png`,
    FILE_SIZE: 97_661,
    EXTENSION: PressKitProfileExtensionEnum.Png,
  },
  EXAMPLE_DOCUMENT: {
    URI: `${S3_URL}/press_kit/common/default-thumbnails/default-pdf-thumbnail.png`,
    FILE_SIZE: 1770,
    EXTENSION: PressKitGuidelineExtensionEnum.Pdf,
  },
  EXAMPLE_PROFILE: {
    URI: `${S3_URL}/press_kit/common/default-thumbnails/default-profile-thumbnail.png`,
    FILE_SIZE: 104_898,
    EXTENSION: PressKitProfileExtensionEnum.Png,
  },
  EXAMPLE_GUIDELINE_FILE: {
    URI: `${S3_URL}/press_kit/common/default-thumbnails/default-pdf-thumbnail.png`,
    FILE_SIZE: 1770,
    EXTENSION: PressKitGuidelineExtensionEnum.Pdf,
  },
};

export const DEFAULT_PDF_THUMBNAIL_URL =
  'https://prcdn.freetls.fastly.net/press_kit/common/document-thumbnail-default.png';

export const TOAST_LIFE = {
  success: 3,
  error: 8,
  alert: 8,
  sp: 5,
} as const; // seconds
export const TOAST_ANIMATION_DURATION = 0.2; // seconds
export const TOAST_SP_VISIBLE_TIME =
  TOAST_LIFE.sp + TOAST_ANIMATION_DURATION * 2;

/* eslint @typescript-eslint/no-unnecessary-type-assertion:warn */
export const HEADER_MIN_WIDTH = 1280;

/** プレスリリースエディター用のURL */
export const PRESS_RELEASE_EDITOR_URL = '/api/press_release_editor.php';

/** メンテナンス時間 */
export const MAINTENANCE_START_DATE_TIME = new Date('2025-03-16T01:00:00');
export const MAINTENANCE_END_DATE_TIME = new Date('2025-03-16T04:00:00');

/** 画像のアップロード上限（10MB） */
export const IMAGE_UPLOAD_MAX_SIZE = 10 * 1024 * 1024;
